import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { CarProfile, Eye, EyeSlash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import DemoBanner from '../../components/DemoBanner';
import BrandLogo from '../../components/BrandLogo';
import { useSystemInfo } from '../../hooks/useSystemInfo';
import { getSystemInfo } from '../../services/api';

const LoginPage = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const { brand_name, brand_tagline } = useSystemInfo();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [demoMode, setDemoMode] = useState(false);

  useEffect(() => {
    getSystemInfo()
      .then((r) => setDemoMode(!!r.data?.demo_mode))
      .catch(() => {});
  }, []);

  // Keep the browser tab title in sync with the (possibly demo) brand name.
  useEffect(() => {
    document.title = `${brand_name} — Order Manager`;
  }, [brand_name]);

  const fillDemoCreds = () => {
    setEmail('admin@vehiclebridge.com');
    setPassword('Admin123!');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await login(email, password);
    
    if (result.success) {
      toast.success('Login successful');
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <DemoBanner />
      <div className="flex-1 flex">
      {/* Left Side - Branded */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#0c1a2e]">
        <div className="absolute inset-0 flex flex-col items-center justify-center p-12">
          <BrandLogo size={256} alt={brand_name} className="mb-8" />
          <h1 className="text-2xl font-bold tracking-tight text-white mb-3">Order Manager</h1>
          <p className="text-base text-white/70 max-w-sm text-center">
            {brand_tagline}
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <BrandLogo size={48} alt={brand_name} />
            <span className="font-bold text-xl tracking-tight">Order Manager</span>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold tracking-tight">{t('auth.welcomeBack')}</h2>
            <p className="text-muted-foreground mt-2">Enter your credentials to access your account</p>
          </div>

          {demoMode && (
            <div
              data-testid="demo-creds-hint"
              className="mb-5 p-3 bg-amber-50 border border-amber-300 rounded text-xs sm:text-sm"
            >
              <p className="font-semibold text-amber-900 mb-1">Demo credentials</p>
              <p className="text-amber-900/80 mb-2">
                Email: <code className="bg-white px-1 rounded">admin@vehiclebridge.com</code><br/>
                Password: <code className="bg-white px-1 rounded">Admin123!</code>
              </p>
              <button
                type="button"
                onClick={fillDemoCreds}
                className="text-amber-900 underline font-medium hover:no-underline"
              >
                Click to autofill
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="input-brutalist"
                data-testid="login-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="input-brutalist pr-10"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end">
              <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                {t('auth.forgotPassword')}
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full btn-primary"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Signing in...' : t('auth.signIn')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              {t('auth.noAccount')}{' '}
              <Link to="/register" className="text-primary font-medium hover:underline">
                {t('auth.signUp')}
              </Link>
            </p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default LoginPage;
